import { Image, Tag } from '@frontend/core';
import { ApiPaginationLinks, ApiPaginationMeta } from '@frontend/shared';

/**
 * The Shows section's records, normalised from the backend's frontend shows
 * API — `docs/api/frontend-shows-api.md` in the backend repository.
 */

/**
 * Where a translation into the requested language stands, as the backend
 * reports it in `meta.translation_state`. Obey `worthPolling` rather than
 * reading progress into the state's name.
 */
export type ShowsTranslationState = 'absent' | 'queued' | 'translating' | 'ready' | 'partial' | 'failed' | 'refused';

export type ShowChannel = 'vimeo' | 'youtube' | 'buzzsprout' | 'spotify' | 'apple_podcasts';

export type CutMedium = 'video' | 'audio';

export type ClipKind = 'trailer' | 'highlight' | 'promo' | 'tool_demo';

export type CrewRole = 'host' | 'co_host' | 'guest' | 'panelist' | 'producer';

export type ToolType = 'exercise' | 'book' | 'website' | 'video' | 'other';

/** The language a record's text arrived in, and where a translation stands. */
export interface Translated {
  requestedLocale: string;
  servedLocale: string;
  translationState: ShowsTranslationState | null;
  /** Whether asking again could bring a different answer: a spinner belongs on the page. */
  worthPolling: boolean;
}

/** Where a recording, or a show, is published. */
export interface ChannelLink {
  channel: ShowChannel;
  url: string;
  externalId: string | null;
  /** The one to embed on our page. */
  isPlayer: boolean;
}

export interface ShowsSubject {
  id: number;
  name: string;
  slug: string;
}

/** Someone who took part, shown under the heading "Credits". A member is named, and nothing more. */
export interface CrewMember {
  id: string;
  role: CrewRole;
  name: string;
  /** A non-member's link, such as their website; never a member's. */
  url: string | null;
  isMember: boolean;
}

export interface Season extends Translated {
  id: string;
  number: number;
  title: string | null;
  description: string | null;
}

export interface Show extends Translated {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  /** Markdown. The detail only. */
  description: string | null;
  language: string | null;
  colours: ShowColours | null;
  publishedAt: Date | null;
  channelLinks: ChannelLink[];
  seasons: Season[];
  crew: CrewMember[];
  images: Image[];
  cultures: ShowsSubject[];
  topics: ShowsSubject[];
  tags: Tag[];
}

/** A show's appearance. Applied on that show's own pages only. */
export interface ShowColours {
  primary: string;
  accent: string;
}

/** One finished edit of an episode: a medium, in one spoken language. */
export interface Cut {
  id: string;
  medium: CutMedium;
  language: string | null;
  durationSeconds: number | null;
  /** The player first. */
  channelLinks: ChannelLink[];
}

export interface ChapterTime {
  /** The cut's id. */
  cut: string;
  startsAtSeconds: number;
}

export interface Chapter extends Translated {
  id: string;
  position: number;
  title: string;
  description: string | null;
  /** Where the chapter starts on each cut it was timed on. */
  times: ChapterTime[];
}

/** An insight or article an editor named as related reading. */
export interface RelatedReading {
  type: 'insights' | 'articles';
  id: string;
  slug: string | null;
  title: string | null;
}

export interface EpisodeLink {
  id: string;
  url: string;
  label: string | null;
  ctaLabel: string | null;
  description: string | null;
}

export interface Quotation {
  id: string;
  text: string | null;
  originator: string | null;
  originatorDescription: string | null;
  source: string | null;
}

export interface Episode extends Translated {
  id: string;
  slug: string;
  /** The show's slug. */
  show: string;
  /** The season's number; `null` for an ad hoc episode. */
  season: number | null;
  number: number | null;
  title: string;
  /** Markdown. */
  summary: string | null;
  /** Markdown. The detail only. */
  notes: string | null;
  language: string | null;
  featured: boolean;
  publishedAt: Date | null;
  related: RelatedReading[];
  cuts: Cut[];
  chapters: Chapter[];
  crew: CrewMember[];
  tools: Tool[];
  clips: Clip[];
  links: EpisodeLink[];
  quotations: Quotation[];
  images: Image[];
  cultures: ShowsSubject[];
  topics: ShowsSubject[];
  tags: Tag[];
}

export type ClipOwner = { type: 'shows'; slug: string } | { type: 'seasons'; number: number } | { type: 'episodes'; slug: string };

export interface Clip extends Translated {
  id: string;
  slug: string;
  show: string;
  kind: ClipKind;
  title: string;
  summary: string | null;
  language: string | null;
  durationSeconds: number | null;
  publishedAt: Date | null;
  owner: ClipOwner | null;
  /** Where in an episode's cut it was taken from. */
  source: { cut: string; startsAtSeconds: number | null } | null;
  channelLinks: ChannelLink[];
  crew: CrewMember[];
  tools: Tool[];
  images: Image[];
}

export interface Tool extends Translated {
  id: string;
  slug: string;
  type: ToolType;
  name: string;
  summary: string | null;
  /** Markdown. The detail only. */
  description: string | null;
  url: string | null;
  /** The names of the members who recommend it. */
  recommendedBy: string[];
  episodes: Episode[];
  /** Its demonstrations, played on its page. */
  clips: Clip[];
  images: Image[];
}

export interface ShowsPage<T> {
  items: T[];
  links: ApiPaginationLinks;
  meta: ApiPaginationMeta;
}

/** The filters and paging a list takes; each list takes only some of them. */
export interface ShowsQuery {
  filter?: {
    show?: string[];
    season?: number[];
    culture?: string[];
    topic?: string[];
    tag?: string[];
    kind?: ClipKind[];
    type?: ToolType[];
    featured?: boolean;
    search?: string;
  };
  sort?: string;
  page?: number;
  pageSize?: number;
  include?: string[];
}

/**
 * Something a reader can like, or ask for in their language: a show, one of its episodes or
 * clips, or a tool.
 */
export type ShowsItemRef =
  | { kind: 'show'; slug: string }
  | { kind: 'episode'; show: string; slug: string }
  | { kind: 'clip'; show: string; slug: string }
  | { kind: 'tool'; slug: string };

/**
 * What an application hands the Shows pages, in its routes' data as `showsSection`: where the
 * section and the tools are mounted, so the pages can link to each other.
 */
export interface ShowsDefinition {
  showsPath: string;
  toolsPath: string;
}

export const DEFAULT_SHOWS_DEFINITION: ShowsDefinition = { showsPath: '/shows', toolsPath: '/tools' };

/**
 * A clip's page. An episode's clip lives under its episode, `/shows/{show}/{episode}/clips/{clip}`,
 * so the breadcrumb passes through the episode; a show's or a season's clip under the show,
 * `/shows/{show}/clips/{clip}`.
 */
export function clipPath(definition: ShowsDefinition, clip: Pick<Clip, 'show' | 'slug' | 'owner'>): string[] {
  return clip.owner?.type === 'episodes'
    ? [definition.showsPath, clip.show, clip.owner.slug, 'clips', clip.slug]
    : [definition.showsPath, clip.show, 'clips', clip.slug];
}
